import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet,
  Plus,
  Loader2,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { Switch } from '@/components/ui/switch';

import { API_URL } from '@/lib/api';

const WalletDues = () => {
  const [walletDues, setWalletDues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [debitModalOpen, setDebitModalOpen] = useState(false);
  const [showAllStores, setShowAllStores] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [debitAmount, setDebitAmount] = useState('');
  const [debitNotes, setDebitNotes] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: '',
    reference_number: '',
    notes: ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchWalletDues();
  }, [showAllStores]);

  const mapWallet = (w) => {
    if (!w || typeof w !== 'object') return null;
    const storeId = w.storeId ?? w.store_id;
    const dueRaw = w.outstandingDue ?? w.outstanding_due ?? w.walletDue ?? w.wallet_due;
    const due = typeof dueRaw === 'number' ? dueRaw : parseFloat(String(dueRaw ?? '0').replace(/,/g, ''), 10);
    return {
      store_id: storeId,
      store_name: w.storeName ?? w.store_name ?? '—',
      total_cod_orders: Number(w.totalCodOrders ?? w.total_cod_orders ?? 0) || 0,
      total_paid: Number(w.totalPaid ?? w.total_paid ?? 0) || 0,
      outstanding_due: Number.isFinite(due) ? due : 0,
    };
  };

  const fetchWalletDues = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wallet-dues`, {
        params: showAllStores ? {} : { onlyOutstanding: 'true' },
      });
      let raw = response.data;
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw);
        } catch {
          raw = [];
        }
      }
      const rows = Array.isArray(raw) ? raw : [];
      setWalletDues(rows.map(mapWallet).filter(Boolean));
    } catch (error) {
      toast.error('Failed to load wallet dues');
      setWalletDues([]);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (store) => {
    setSelectedStore(store);
    setFormData({
      amount: '',
      payment_method: '',
      reference_number: '',
      notes: ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedStore(null);
    setFormData({
      amount: '',
      payment_method: '',
      reference_number: '',
      notes: ''
    });
  };

  const openDebitModal = (store) => {
    setSelectedStore(store);
    setDebitAmount('');
    setDebitNotes('');
    setDebitModalOpen(true);
  };

  const closeDebitModal = () => {
    setDebitModalOpen(false);
    setSelectedStore(null);
    setDebitAmount('');
    setDebitNotes('');
  };

  const submitDebit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(debitAmount, 10);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/wallet-adjust`, {
        storeId: selectedStore.store_id,
        amount: amt,
        type: 'debit',
        note: debitNotes || 'Admin correction: increase balance due',
      });
      toast.success('Balance due increased');
      closeDebitModal();
      fetchWalletDues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(formData.amount);
    if (!amt || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      // Use the improved record-payment API for clean logging
      await axios.post(`${API_URL}/stores/${selectedStore.store_id}/record-payment`, {
        amount: amt,
        note: [formData.payment_method, formData.reference_number, formData.notes].filter(Boolean).join(' | ')
      });
      toast.success('Payment recorded — wallet updated');
      closeModal();
      fetchWalletDues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const totalOutstanding = walletDues.reduce((sum, item) => sum + item.outstanding_due, 0);

  return (
    <div data-testid="wallet-dues-page">
      {/* Header */}
      <div className="page-header flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="page-title">Wallet Dues</h1>
          <p className="text-slate-500 mt-1">Per-store COD volume, collections, and current wallet balance due</p>
          <div className="flex items-center gap-2 mt-3">
            <Switch id="show-all" checked={showAllStores} onCheckedChange={setShowAllStores} />
            <Label htmlFor="show-all" className="text-sm text-slate-600 cursor-pointer">
              Show all stores (including zero due)
            </Label>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-500">Total Outstanding (visible rows)</p>
          <p className="text-2xl font-bold text-rose-600">₹{totalOutstanding.toLocaleString()}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
          </div>
        ) : walletDues.length === 0 ? (
          <div className="empty-state">
            <Wallet className="w-12 h-12 mb-3 text-slate-300" />
            <p className="text-lg font-medium">{showAllStores ? 'No stores' : 'No outstanding dues'}</p>
            <p className="text-sm mt-1">
              {showAllStores
                ? 'Add a store to track wallet balances'
                : 'All COD collections are up to date — toggle “Show all stores” to see zero-balance accounts'}
            </p>
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Store Name</TableHead>
                <TableHead>Outstanding Due</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {walletDues.slice((page - 1) * pageSize, page * pageSize).map((item) => (
                <TableRow key={item.store_id} className="table-row-hover" data-testid={`wallet-row-${item.store_id}`}>
                  <TableCell className="font-medium">{item.store_name}</TableCell>
                  <TableCell className={item.outstanding_due > 0 ? 'text-rose-600 font-black' : 'text-slate-500'}>
                    ₹{item.outstanding_due.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button 
                        size="sm"
                        onClick={() => openModal(item)}
                        disabled={item.outstanding_due <= 0}
                        className="bg-purple-950 hover:bg-purple-900 disabled:opacity-50"
                        data-testid={`record-payment-${item.store_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Record payment
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDebitModal(item)}
                        className="border-amber-200 text-amber-900 hover:bg-amber-50"
                        data-testid={`increase-due-${item.store_id}`}
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Increase due
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            data={walletDues}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
          />
          </>
        )}
      </div>

      {/* Record Payment Modal */}
      <Dialog open={modalOpen} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Wallet Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            <div>
              <Label className="form-label">Store</Label>
              <Input value={selectedStore?.store_name || ''} disabled />
            </div>
            <div>
              <Label className="form-label">Outstanding Due</Label>
              <Input 
                value={`₹${selectedStore?.outstanding_due?.toLocaleString() || 0}`} 
                disabled 
                className="text-rose-600 font-medium"
              />
            </div>
            <div>
              <Label htmlFor="amount" className="form-label">Amount Paid *</Label>
              <Input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                min="0"
                max={selectedStore?.outstanding_due || 0}
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                required
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="payment_method" className="form-label">Payment Method *</Label>
              <Select 
                value={formData.payment_method} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reference_number" className="form-label">Reference Number</Label>
              <Input
                id="reference_number"
                name="reference_number"
                value={formData.reference_number}
                onChange={handleChange}
                placeholder="Transaction/Cheque number"
                data-testid="reference-input"
              />
            </div>
            <div>
              <Label htmlFor="notes" className="form-label">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Additional notes"
                rows={2}
                data-testid="notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-purple-950 hover:bg-purple-900"
                disabled={saving}
                data-testid="submit-payment-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Recording...
                  </>
                ) : (
                  'Record Payment'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={debitModalOpen} onOpenChange={(o) => !o && closeDebitModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Increase balance due (correction)</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDebit} className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Adds to the store&apos;s COD wallet due (<code className="text-xs bg-slate-100 px-1 rounded">wallet-adjust</code> debit).
              Use for missed COD bookings or corrections.
            </p>
            <div>
              <Label>Store</Label>
              <Input value={selectedStore?.store_name || ''} disabled />
            </div>
            <div>
              <Label htmlFor="debit-amt">Amount to add *</Label>
              <Input
                id="debit-amt"
                type="number"
                step="0.01"
                min="0"
                value={debitAmount}
                onChange={(e) => setDebitAmount(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="debit-notes">Notes</Label>
              <Textarea
                id="debit-notes"
                value={debitNotes}
                onChange={(e) => setDebitNotes(e.target.value)}
                rows={2}
                placeholder="Reason for adjustment"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDebitModal}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-amber-700 hover:bg-amber-800 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply debit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletDues;
